import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { cardSizeFrom } from "@/board/board-utils";
import { boardOf } from "@/board/boardFactory";
import type { BoardLocation } from "@/board/location/BoardLocation";
import { SelectionLocation } from "@/board/location/SelectionLocation";
import { emitStickyChanged, sendStickyNoteAction } from "@/composables/useEventBus";
import { addBreadcrumb } from "@/error/sentry";
import { relativeFromViewport } from "@/math/coordinate-utils";
import { divided } from "@/math/coordinates";
import { defaultCardSizeFactor } from "@/model/Settings";
import type { Id } from "@/model/baseTypes";
import type { Board, BoardData } from "@/model/board";
import { MAX_FINISHED_TICKS } from "@/model/board";
import type { BoardCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { startUserHeartbeat, stopUserHeartbeat } from "@/utils/heartbeat";
import { asReadonly, asSemiReadonly } from "@/utils/vue";

import { useBoardsStore } from "./boards";
import { useContextMenuStore } from "./contextMenu";
import { toggleFunc } from "./helpers/toggle";
import { usePointerStore } from "./pointer";
import { useUsersOnBoardStore } from "./usersOnBoard";

class BoardNotFoundError extends Error {}

export function isEmptyBoard(board: Board) {
  return board.id === "0";
}

export function emptyBoard(): BoardData<"team"> {
  return {
    id: "0",
    type: "team",
    team: { id: "", name: "empty" },
    cards: {},
    cardSize: cardSizeFrom({ factor: defaultCardSizeFactor }),
    maxZ: 0,
    state: { state: "loaded", finishedTicks: MAX_FINISHED_TICKS, loadedAt: 1 },
    iterationStates: [emptyIterationState(), emptyIterationState()],
    almSources: [],
    selected: {},
    shapes: [],
    objectives: [],
    stretchObjectives: [],
    objectivesResolved: true,
  };
}

export function emptyIterationState() {
  return { status: null, detail: null };
}

export function cardSize(): RelativeCoordinate {
  return useBoardStore().currentBoard().cardSize;
}

export function halfCardSize(): RelativeCoordinate {
  return divided(cardSize(), 2);
}

export const useBoardStore = defineStore("board", () => {
  const board = ref<Board>();
  const activeCardId = ref<string | null>(null);
  const enlargedStickyNoteId = ref<string | null>(null);
  const magnifying = ref(false);
  const scaleDropdownOpen = ref(false);
  const forceFullDetailsForBoardId = ref<string | null>(null);

  const baseBoard = computed(() => boardOf(board.value));
  // sticky types that can possibly appear on the board
  const possibleStickyTypes = computed(() => baseBoard.value.possibleStickyTypes);
  // sticky types that can be used to create a new sticky note
  const creatableStickyTypes = computed(() => baseBoard.value.creatableStickyTypes);
  const showRiskyLinks = computed(() => baseBoard.value.showRiskyLinks);

  const hasCurrentBoard = computed(() => board.value !== undefined);
  const selectedStickyIds = computed(() => Object.keys(board.value?.selected || {}));
  const selectedStickies = computed(() =>
    selectedStickyIds.value.flatMap((id) =>
      currentBoard().cards[id] ? [currentBoard().cards[id]] : [],
    ),
  );
  const selectedOrActiveStickies = computed(() =>
    selectedStickyIds.value.length > 0
      ? selectedStickies.value
      : activeCard.value
        ? [activeCard.value]
        : [],
  );
  const activeCard = computed(() =>
    activeCardId.value ? currentBoard().cards[activeCardId.value] : null,
  );

  function currentBoard() {
    if (board.value) {
      return board.value;
    }
    throw new BoardNotFoundError("No current board is set");
  }

  function boardLocation(pos: RelativeCoordinate | number | string, top?: number): BoardLocation {
    return baseBoard.value.location(pos, top);
  }

  function switchBoard(target?: Board) {
    const newBoard =
      target || (board.value && useBoardsStore().boardByTypeOptional(board.value.type));
    const res = { switched: false, board: board.value, cards: new Array<BoardCard>() };
    if (newBoard && newBoard.id !== board.value?.id) {
      res.switched = true;
      if (hasCurrentBoard.value) {
        res.cards = leaveBoard().cards;
      }
      board.value = newBoard;
      startUserHeartbeat();
    }
    addBreadcrumb("navigation", {
      message: "switch board",
      data: { switched: res.switched, boardId: res.board?.id, boardType: res.board?.type },
    });
    return res;
  }

  function leaveBoard() {
    const current = currentBoard();
    setActiveCardId(null);
    const cards = [];
    for (const id in current.cards) {
      const card = current.cards[id];
      if (card.meta.editing) {
        cards.push(card);
        card.meta.editing = false;
      }
    }
    stopUserHeartbeat();
    usePointerStore().reset();
    useUsersOnBoardStore().resetUsers();
    return { board: current, cards };
  }

  function selectSticky(stickyId: string) {
    if (currentBoard().cards[stickyId]) {
      currentBoard().selected[stickyId] = true;
    }
  }

  function setActiveCard(e: Id & { active: boolean }) {
    // don't clear active card if a different one is active
    if (e.active || activeCardId.value === e.id) {
      setActiveCardId(e.active ? e.id : null);
    }
  }

  function setActiveCardId(cardId: string | null) {
    const prev = activeCardId.value;
    if (prev !== cardId) {
      activeCardId.value = cardId;
      if (prev) {
        sendStickyNoteAction(prev, { action: "deactivate" });
        if (cardId === null) {
          emitStickyChanged(prev);
        }
      }
    }
  }

  function contextMenuRegionStickies(): BoardCard[] {
    const contextPos = useContextMenuStore().menu?.props.position;
    if (!contextPos) {
      return [];
    }
    const loc = baseBoard.value.location(relativeFromViewport(contextPos));
    return loc.cards(currentBoard());
  }

  return {
    // to get the current board as writable, use `currentBoard`
    board: asSemiReadonly(board),
    activeCardId: asReadonly(activeCardId),
    enlargedStickyNoteId: asReadonly(enlargedStickyNoteId),
    magnifying: asReadonly(magnifying),
    scaleDropdownOpen: asReadonly(scaleDropdownOpen),
    forceFullDetailsForBoardId: asReadonly(forceFullDetailsForBoardId),

    baseBoard,
    possibleStickyTypes,
    creatableStickyTypes,
    showRiskyLinks,
    hasCurrentBoard,
    selectedStickyIds,
    selectedStickies,
    selectedOrActiveStickies,
    areMultipleStickiesSelected: computed(() => selectedStickyIds.value.length > 1),
    activeCard,

    currentBoard,
    positionalCardProperties: (pos: RelativeCoordinate) =>
      baseBoard.value.positionalCardProperties(pos),
    boardLocation,
    boardLocationById: (index: string): BoardLocation =>
      SelectionLocation.ofId(index) || boardLocation(index),
    boardId: () => currentBoard().id,
    isStickySelected: (cardId: string) => cardId in (board.value?.selected || {}),
    isStickyIdSelectedOrActive: (id: string) =>
      id in (board.value?.selected || {}) || activeCardId.value === id,
    setBoard: (value: Board) => (board.value = value),
    setForceFullDetailsForBoardId: (id: string | null) => (forceFullDetailsForBoardId.value = id),
    setEnlargedStickyNoteId: (id: string | null) => (enlargedStickyNoteId.value = id),
    setScaleDropdownOpen: (open: boolean) => (scaleDropdownOpen.value = open),
    toggleStickyMagnify: toggleFunc(magnifying),
    switchBoard,
    leaveBoard,
    selectSticky,
    setSelectedStickies: (stickyIds: string[]) =>
      (currentBoard().selected = Object.fromEntries([...stickyIds].map((id) => [id, true]))),
    unselectSticky: (stickyId: string, target?: Board) =>
      delete (target ?? currentBoard()).selected[stickyId],
    setActiveCard,
    setActiveCardId,
    contextMenuRegionStickies,
  };
});
